import React, { useCallback, useEffect, useState } from "react";

import moment, { Moment } from "moment";
import Select, { SingleValue, MultiValue } from "react-select";
import { DateTimePickerComponent } from "@syncfusion/ej2-react-calendars";

import LineChart from "../../components/Graph/LineChart";
import { selectTheme } from "../../util/select-theme";
import { useSelector } from "react-redux";
import { RootState } from "../../app/store";
import { getGraphHourAvgHistorical } from "../../api/getGraphHourAvgHistorical";
import { VscLoading } from "react-icons/vsc";

export const timeinterval = [
  { value: "15-minutes", label: "15 Minute" },
  { value: "hourly", label: "Hourly" },
  { value: "24-hours", label: "24-Hours" },
];

const historical_graph_dataset = (station: any) => {
  return {
    start_time: moment().subtract(1, "day"),
    end_time: moment(),
    station: station,
    time_interval: { value: "15-minutes", label: "15 Minute" },
    yaxis: [
      { label: "SO₂", value: "so2" },
      { label: "PM2.5", value: "pm25" },
    ],
  };
};

// ----------------------
// Interfaces
// ----------------------
interface Station {
  label: string;
  value: string;
}

interface TimeInterval {
  label: string;
  value: string;
}

interface YAxisOption {
  label: string;
  value: string;
}

interface LoginData {
  station: string;
  allStations: Station[];
  analyzer_dropdown: YAxisOption[];
  role: string;
}

interface FormData {
  station: Station | null;
  time_interval: TimeInterval | null;
  start_time: Moment | null;
  end_time: Moment | null;
  yaxis: YAxisOption[];
}

interface ChartData {
  [key: string]: any; // refine based on API
}

export const analyzerOptions = [
  { label: "SO₂", value: "so2" },
  { label: "NO₂", value: "no2" },
  { label: "PM1", value: "pm1" },
  { label: "PM2.5", value: "pm25" },
  { label: "PM10", value: "pm10" },
  { label: "Temperature", value: "temp" },
  { label: "Humidity", value: "humi" },
  { label: "Wind Speed", value: "wind_speed" },
  { label: "Wind Direction", value: "wind_direction" },
];

const HistoricalGraph: React.FC = () => {
  const [theme, setTheme] = useState<"light" | "dark">("light"); // optional: implement your own theme provider
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState<FormData>(
    historical_graph_dataset("")
  );

  const [data, setData] = useState<ChartData[]>([]);

  const devices = useSelector((state: RootState) => state.devices);

  useEffect(() => {
    setFormData(
      historical_graph_dataset({
        label: devices[0].location,
        value: devices[0].id,
      })
    );
  }, []);

  const onChangeHandler = useCallback(
    (
      value: SingleValue<Station | TimeInterval> | Moment | null,
      fieldName: string
    ) => {
      setFormData((prevState) => ({
        ...prevState,
        [fieldName]: value,
      }));
    },
    []
  );

  const fetchHistoricalGraphData = useCallback(() => {
    if (
      !formData?.yaxis?.length ||
      !formData.station?.value ||
      !formData.start_time ||
      !formData.end_time ||
      !formData.time_interval?.value
    ) {
      return;
    }

    try {
      const fetchData = async () => {
        setLoading(true);

        const response = await getGraphHourAvgHistorical(
          formData.station!.value,
          formData.start_time!,
          formData.end_time!,
          formData.time_interval!.value,
          formData.yaxis.map((d) => d.value)
        );
        if (response.data.status === "success") {
          console.log("API response data:", response.data);
          setData(response?.data?.data || []);
        } else {
          console.error("API returned error status:", response.data.status);
          setData([]);
        }
        setLoading(false);
      };

      fetchData();
    } catch (error) {
      console.error("Error fetching data:", error);
      setData([]);
    }
  }, [formData]);

  useEffect(() => {
    fetchHistoricalGraphData();
  }, [
    formData.station,
    formData.start_time,
    formData.end_time,
    formData.time_interval,
    formData.yaxis,
    fetchHistoricalGraphData,
  ]);

  console.log("screen width ==>", window.screen.width);

  return (
    <div className="w-full rounded-xl px-2 min-h-[100%] relative">
      {/* {isPending && <LinearLoader />} */}
      <div className="pt-2">
        <div className="px-2 grid grid-cols-4 gap-x-2 border p-2 rounded-md bg-slate-50 dark:bg-slate-800">
          <div>
            <div className="text-sm font-semibold p-1">Device</div>
            <Select
              options={devices.map((device) => ({
                label: device.location,
                value: device.id,
              }))}
              value={formData.station}
              styles={theme === "dark" ? selectTheme : {}}
              onChange={(selected: SingleValue<Station>) =>
                onChangeHandler(selected, "station")
              }
            />
          </div>
          <div>
            <div className="text-sm font-semibold p-1">Time Interval</div>
            <Select
              options={timeinterval}
              value={formData.time_interval}
              styles={theme === "dark" ? selectTheme : {}}
              onChange={(selected: SingleValue<TimeInterval>) =>
                onChangeHandler(selected, "time_interval")
              }
            />
          </div>
          <div>
            <div className="text-sm font-semibold truncate p-1 ">
              Start Date & Time
            </div>
            <div className="bg-white border border-gray-300 rounded-md">
              <DateTimePickerComponent
                style={{ padding: 5, paddingBottom: 3 }}
                placeholder="Select a start date and time"
                value={formData?.start_time?.toDate()}
                change={(e) =>
                  onChangeHandler(
                    e.value ? moment(e.value) : null,
                    "start_time"
                  )
                }
                openOnFocus
              />
            </div>
          </div>
          <div>
            <div className="text-sm font-semibold truncate p-1">
              End Date & Time
            </div>
            <div className="bg-white  border border-gray-300 rounded-md">
              <DateTimePickerComponent
                style={{ padding: 5, paddingBottom: 3 }}
                placeholder="Select an end date and time"
                value={formData.end_time?.toDate()}
                change={(e) =>
                  onChangeHandler(e.value ? moment(e.value) : null, "end_time")
                }
                openOnFocus
              />
            </div>
          </div>
          <div className="sm:col-span-4">
            <div className="text-sm font-semibold py-1">Parameters</div>
            <Select
              isMulti
              options={analyzerOptions}
              value={formData.yaxis}
              styles={theme === "dark" ? selectTheme : {}}
              onChange={(selected: MultiValue<YAxisOption>) =>
                setFormData({
                  ...formData,
                  yaxis: (selected as YAxisOption[]) || [],
                })
              }
              // isOptionDisabled={() => formData.yaxis.length >= 8}
              isClearable={formData.yaxis.length > 1}
            />
          </div>
        </div>
      </div>

      {!!loading ? (
        <div className="flex items-center justify-center my-auto h-96">
          <VscLoading className="animate-spin" size="150px" />
        </div>
      ) : (
        <div
          className="ml-3  mt-2 bg-white shadow-sm  rounded-md p-2 "
          style={{
            width: "98.6%",
            height: window.screen.width > 1700 ? 700 : 550,
          }}
        >
          {formData.yaxis?.length > 0 ? (
            data?.length > 0 ? (
              <LineChart dataset={data as any} formData={formData} id={0} />
            ) : (
              <div className="text-center flex justify-center items-center h-full font-semibold mx-10 text-xl text-orange-700">
                No data available for the selected device, parameters and time
                range.
              </div>
            )
          ) : (
            <div className="text-center flex justify-center items-center h-full font-semibold mx-10 text-xl text-orange-700">
              Please select at least one parameter for the Y-axis.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default HistoricalGraph;
